/**
 * Derive an artifact-canvas reference from a completed harness tool use.
 *
 * Moved out of the (deleted) polling adapter and re-keyed off the
 * harness `ToolUseItem` (which merges call + result). Most artifact ids
 * live on the tool call's `input` (`session_id`, `patient_id`); a few
 * (`create_session`, the export/generate family) read the new id from
 * the result JSON. Tools the canvas doesn't handle return `undefined` —
 * the timeline row just renders without an "Open" affordance.
 *
 * Extracted from `src/features/projects/harness/artifactFromTool.ts`, which
 * stays in place as the app-side binder of {@link ArtifactRefPorts} —
 * history: `git log --follow src/features/projects/harness/artifactFromTool.ts`.
 *
 * This module must not import `tool-registry.ts` — the registry owns
 * `artifactFromToolUse` and imports {@link ARTIFACT_REFS} from here.
 */

import type { TabType } from '@heidi/session/contracts';

import {
  parseConnectedCalendarPayload,
  parseConnectedFilesPayload,
  parseConnectedMailPayload,
} from '../../../../contracts/connectedAppsArtifacts';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import {
  directoryLeafTool,
  isEligibilityLeafTool,
  parseEligibilityPayload,
} from '../../../../contracts/insuranceArtifacts';
import { isRecord } from '../../../../contracts/parse-utils';
import {
  readToolResultJsonCandidates,
  readToolResultText,
} from '../../../../contracts/toolResult';
import type { Artifact } from '../../artifacts/state';

export interface ArtifactRefPorts {
  /** PostHog-resolved app flag; a thunk so a mid-session flip is honoured. */
  isSlideBuilderEnabled: () => boolean;
  /** Session tab values the derivers pin — `TabType` is a foreign-domain enum, so the app resolves them. */
  sessionTabs: { context: TabType; document: TabType };
  /** Validates a wire `active_tab` string against the session tab enum; `undefined` when it is not one. */
  parseSessionTab: (value: string) => TabType | undefined;
}

export type ArtifactRef = (
  item: ToolUseItem,
  ports: ArtifactRefPorts
) => Artifact | undefined;

export interface ToolArtifactSpec {
  /** Declared kind — what the panel opens to. */
  type: Artifact['type'];
  /** Resolves the concrete ref from a completed tool use, or `undefined` when the result carries no usable id. */
  ref: ArtifactRef;
}

const artifactRef = <T extends Artifact['type']>(
  type: T,
  ref: (
    item: ToolUseItem,
    ports: ArtifactRefPorts
  ) => Extract<Artifact, { type: T }> | undefined
): ToolArtifactSpec => ({ type, ref });

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const readTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

/**
 * The structured result-provider metadata frame (`result_provider_metadata.data`)
 * as a record, or ``undefined`` when absent/non-object. Local (not
 * `toolResult.ts`'s `isRecord`) to avoid an import cycle: `toolResult.ts` already
 * imports `toolResultText` from this module.
 */
const metadataData = (item: ToolUseItem): Record<string, unknown> | undefined => {
  const data = item.result_provider_metadata?.data;
  return typeof data === 'object' && data !== null
    ? (data as Record<string, unknown>)
    : undefined;
};

/**
 * `presentation_id` from a pptx tool result's structured metadata — the same
 * field the pptx card adapter reads.
 */
const presentationIdFromMetadata = (item: ToolUseItem): string | undefined =>
  str(metadataData(item)?.presentation_id);

/**
 * `presentation_id` from an `add_slides_to_presentation` result, whose metadata
 * is `{ success, slides: [slide_update, …] }` with the id on each slide rather
 * than at the top level. Result-derived only — never the model-written input.
 */
const presentationIdFromAddedSlides = (item: ToolUseItem): string | undefined => {
  const slides = metadataData(item)?.slides;
  if (!Array.isArray(slides)) return undefined;
  for (const slide of slides) {
    const id =
      typeof slide === 'object' && slide !== null
        ? str((slide as Record<string, unknown>).presentation_id)
        : undefined;
    if (id) return id;
  }
  return undefined;
};

/** ``memory_id`` from save_memory structured result metadata. */
const memoryIdFromMetadata = (item: ToolUseItem): string | undefined =>
  str(metadataData(item)?.memory_id);

/**
 * ``template_id`` + ``app_name`` from a `get_note_push_hints` structured result
 * (`result_provider_metadata.data` — {@link NotePushHintsData}). Both keys are
 * required to address the note-push candidate mapping; returns ``undefined`` if
 * either is absent.
 */
const notePushHintKeysFromMetadata = (
  item: ToolUseItem
): { templateId: string; appName: string } | undefined => {
  const record = metadataData(item);
  const templateId = str(record?.template_id);
  const appName = str(record?.app_name);
  return templateId && appName ? { templateId, appName } : undefined;
};

const parseJsonRecord = (
  text: string | undefined
): Record<string, unknown> | undefined => {
  if (!text) return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Resolve an id from a tool RESULT payload's structured JSON fields.
 *
 * Reads the authoritative keys the backend echoes (e.g. `create_session`
 * returns `{"session_id": "…"}`) as strings, in order. Returns `undefined`
 * when the result is missing, not a JSON record, or none of the keys are
 * a non-empty string. A confirmation sentence is not a source of ids.
 */
const idFromResult = (
  text: string | undefined,
  ...keys: string[]
): string | undefined => {
  const parsed = parseJsonRecord(text);
  if (!parsed) return undefined;
  for (const key of keys) {
    const id = str(parsed[key]);
    if (id) return id;
  }
  return undefined;
};

/**
 * Read a single id field straight from a result's JSON. For a tool whose
 * validated INPUT already carries the authoritative id, a confirmation
 * sentence must not contribute an id. Returns `undefined` for non-JSON /
 * missing field so the caller falls back to the trusted input.
 */
const jsonFieldId = (text: string | undefined, key: string): string | undefined => {
  const parsed = parseJsonRecord(text);
  return parsed ? str(parsed[key]) : undefined;
};

/**
 * Patient-profile id out of a patient tool's RESULT payload.
 *
 * The patient tools (`update_patient_context`, `create_patient_profile`)
 * deliberately don't take the profile id on their INPUT — the backend
 * resolves it from the runtime target context (or an `ehr_patient_id`)
 * and echoes the resolved id back on the result as `patient_profile_id`.
 * So unlike the session tools we read the id from the result, not the
 * call input.
 */
const patientIdFromResult = (text: string | undefined): string | undefined =>
  idFromResult(text, 'patient_profile_id', 'patient_id');

/**
 * Local stand-in for the app's `readNotePushRecordedData`. That helper
 * runs `coerceIdentified` over `readToolResultJsonCandidates` and returns
 * the first record whose `template_id` and `app_name` are both strings —
 * exactly this loop.
 */
const readNotePushRecordedData = (
  item: ToolUseItem
): { template_id: string; app_name: string } | undefined => {
  for (const candidate of readToolResultJsonCandidates(item)) {
    const templateId = candidate.template_id;
    const appName = candidate.app_name;
    if (typeof templateId === 'string' && typeof appName === 'string') {
      return { template_id: templateId, app_name: appName };
    }
  }
  return undefined;
};

type LetterArtifact = Extract<Artifact, { type: 'letter' }>;
type SessionArtifact = Extract<Artifact, { type: 'session' }>;
type WorkflowArtifact = Extract<Artifact, { type: 'workflow' }>;
type CalendarArtifact = Extract<Artifact, { type: 'calendar' }>;
type MailArtifact = Extract<Artifact, { type: 'mail' }>;
type FilesArtifact = Extract<Artifact, { type: 'files' }>;
type InsuranceArtifact = Extract<Artifact, { type: 'insurance' }>;

const letterArtifactFromResult = (item: ToolUseItem): LetterArtifact | undefined => {
  const metadata = metadataData(item);
  const result = parseJsonRecord(toolResultText(item));
  const sessionId =
    readTrimmedString(metadata?.session_id) ?? readTrimmedString(result?.session_id);
  const letterId =
    readTrimmedString(metadata?.letter_id) ?? readTrimmedString(result?.letter_id);
  return sessionId && letterId ? { type: 'letter', sessionId, letterId } : undefined;
};

const letterArtifactMatchingInputIds = (
  item: ToolUseItem
): LetterArtifact | undefined => {
  const artifact = letterArtifactFromResult(item);
  if (!artifact) return undefined;
  const expectedSessionId = readTrimmedString(item.input.session_id);
  const expectedLetterId = readTrimmedString(item.input.letter_id);
  if (!expectedSessionId || !expectedLetterId) return undefined;
  if (
    artifact.sessionId !== expectedSessionId ||
    artifact.letterId !== expectedLetterId
  ) {
    return undefined;
  }
  return artifact;
};

/**
 * Open the session pinned to the Context tab (where context-document chips
 * and the clinician-notes editor both live). The session_id is carried on
 * the tool input for every context-document / clinician-notes write, so a
 * single shared deriver covers them all.
 */
const openSessionContextTab = (
  item: ToolUseItem,
  ports: ArtifactRefPorts
): SessionArtifact | undefined => {
  const sessionId = str(item.input.session_id);
  return sessionId
    ? { type: 'session', sessionId, focusTab: ports.sessionTabs.context }
    : undefined;
};

/** Result JSON `workflow_id` first; schedule/trigger results are plain text. */
const workflowFromResultJsonOrInput = (
  item: ToolUseItem
): WorkflowArtifact | undefined => {
  const workflowId =
    jsonFieldId(toolResultText(item), 'workflow_id') ?? str(item.input.workflow_id);
  return workflowId ? { type: 'workflow', workflowId } : undefined;
};

const generatedSessionDocument = (
  item: ToolUseItem,
  ports: ArtifactRefPorts
): SessionArtifact | undefined => {
  const result = parseJsonRecord(toolResultText(item));
  const sessionId = str(item.input.session_id) ?? str(result?.session_id);
  const documentId = str(result?.document_id);
  return sessionId && documentId
    ? {
        type: 'session',
        sessionId,
        focusTab: ports.sessionTabs.document,
        focusDocumentId: documentId,
      }
    : undefined;
};

const createSessionFromResult = (item: ToolUseItem): SessionArtifact | undefined => {
  const sessionId = idFromResult(toolResultText(item), 'session_id');
  return sessionId ? { type: 'session', sessionId } : undefined;
};

const documentFromSessionResult = (
  item: ToolUseItem
): Extract<Artifact, { type: 'document' }> | undefined => {
  const sessionId = str(item.input.session_id);
  const documentId = idFromResult(toolResultText(item), 'document_id');
  return sessionId && documentId
    ? { type: 'document', sessionId, documentId }
    : undefined;
};

/**
 * An artifact from `{ type, ...ids }` fields: `open_artifact`'s result JSON,
 * or the `artifact` envelope any tool can put on its result metadata.
 */
const artifactFromFields = (
  fields: Record<string, unknown> | undefined,
  ports: ArtifactRefPorts
): Artifact | undefined => {
  const type = str(fields?.type);
  if (!fields || !type) return undefined;

  const sessionId = str(fields.session_id);
  const patientId = str(fields.patient_id);
  const documentId = str(fields.document_id);
  const letterId = str(fields.letter_id);
  const presentationId = str(fields.presentation_id);
  const memoryId = str(fields.memory_id);
  const workflowId = str(fields.workflow_id);
  const activeTabRaw = str(fields.active_tab);
  const focusTab = activeTabRaw ? ports.parseSessionTab(activeTabRaw) : undefined;

  switch (type) {
    case 'session':
      return sessionId
        ? { type: 'session', sessionId, ...(focusTab ? { focusTab } : {}) }
        : undefined;
    case 'note':
      return sessionId ? { type: 'note', sessionId } : undefined;
    case 'document':
      return sessionId && documentId
        ? { type: 'document', sessionId, documentId }
        : undefined;
    case 'letter':
      return sessionId && letterId ? { type: 'letter', sessionId, letterId } : undefined;
    case 'patient':
      return patientId ? { type: 'patient', patientId } : undefined;
    case 'presentation':
      // Same release gate as generate_pptx — do not open Slide Builder when
      // the flag is off.
      if (!ports.isSlideBuilderEnabled()) return undefined;
      return presentationId ? { type: 'presentation', presentationId } : undefined;
    case 'memory':
      return memoryId ? { type: 'memory', memoryId } : undefined;
    case 'workflow':
      return workflowId ? { type: 'workflow', workflowId } : undefined;
    case 'session-list':
      // XXX: a preview is one object (AGE-2417); session lists stay as chat result cards.
      return undefined;
    default:
      return undefined;
  }
};

const openArtifactRef: ArtifactRef = (item, ports) =>
  artifactFromFields(parseJsonRecord(toolResultText(item)), ports);

/**
 * The artifact a tool without its own {@link ARTIFACT_REFS} entry opens, from
 * `result_provider_metadata.data.artifact`. Only that key is read, so a bare
 * `type` field on some other result never opens a panel.
 */
export const artifactFromResultEnvelope: ArtifactRef = (item, ports) => {
  const envelope = metadataData(item)?.artifact;
  return isRecord(envelope) ? artifactFromFields(envelope, ports) : undefined;
};

const calendarArtifactFromTool = (item: ToolUseItem): CalendarArtifact | undefined => {
  const data = metadataData(item);
  const input =
    typeof item.input === 'object' && item.input !== null ? item.input : undefined;
  const payload = parseConnectedCalendarPayload(data, input);
  if (!payload) return undefined;
  // Open empty windows when the tool returned a warning / calendar_ids so
  // Merge vs Nango empties are inspectable in the canvas debug strip.
  if (
    payload.events.length === 0 &&
    !payload.warning &&
    !(payload.calendarIds && payload.calendarIds.length > 0)
  ) {
    return undefined;
  }
  return {
    type: 'calendar',
    events: payload.events,
    provider: payload.provider,
    start: payload.start,
    end: payload.end,
    calendarIds: payload.calendarIds,
    warning: payload.warning,
  };
};

const mailArtifactFromTool = (item: ToolUseItem): MailArtifact | undefined => {
  const data = metadataData(item);
  const input =
    typeof item.input === 'object' && item.input !== null ? item.input : undefined;
  const payload = parseConnectedMailPayload(data, input);
  if (!payload || payload.messages.length === 0) return undefined;
  return {
    type: 'mail',
    messages: payload.messages,
    provider: payload.provider,
    query: payload.query,
  };
};

const filesArtifactFromTool = (item: ToolUseItem): FilesArtifact | undefined => {
  const data = metadataData(item);
  const input =
    typeof item.input === 'object' && item.input !== null ? item.input : undefined;
  const payload = parseConnectedFilesPayload(data, input);
  if (!payload || payload.files.length === 0) return undefined;
  return {
    type: 'files',
    files: payload.files,
    provider: payload.provider,
    query: payload.query,
  };
};

const insuranceArtifactFromTool = (item: ToolUseItem): InsuranceArtifact | undefined => {
  const leaf = directoryLeafTool(item.name, item.input);
  if (!isEligibilityLeafTool(leaf)) return undefined;
  const data = metadataData(item);
  const input =
    typeof item.input === 'object' && item.input !== null ? item.input : undefined;
  const payload = parseEligibilityPayload(data, input);
  if (!payload) return undefined;
  if (payload.patients.length === 0 && !payload.warning) return undefined;
  return {
    type: 'insurance',
    variant: 'eligibility',
    patients: payload.patients,
    provider: payload.provider,
    query: payload.query,
    warning: payload.warning,
  };
};

/**
 * Per-tool artifact declaration: the panel kind plus the ref that
 * resolves a concrete artifact from a completed tool use. Adding a new
 * tool is one map entry here; the `ArtifactPanelRouter` switch then
 * exhaustively covers any new `Artifact` variant. Tools without a
 * meaningful artifact (web_search, calculators, etc.) are intentionally
 * absent. The registry assigns these specs onto `artifact` so a tool
 * opens the panel iff it declares one.
 */
export const ARTIFACT_REFS: Readonly<Record<string, ToolArtifactSpec>> = {
  // A bare create names the whole session, not one surface: the card links to
  // it (with the focused pane on, the link is the full Session page) and the
  // harness never auto-opens it — see `isGenericSessionArtifact`.
  create_session: artifactRef('session', createSessionFromResult),
  create_session_for_member: artifactRef('session', createSessionFromResult),
  // Reads the clinician may want to open: surface an "Open" affordance so
  // viewing a (possibly teammate's) session or its note from chat can pop the
  // artifact panel. The session/note loaders enforce view permission, so an
  // unauthorized session simply won't load.
  get_session: artifactRef('session', (item) => {
    const sessionId = str(item.input.session_id);
    return sessionId ? { type: 'session', sessionId } : undefined;
  }),
  set_session_status: artifactRef('session', (item) => {
    const sessionId = str(item.input.session_id);
    return sessionId ? { type: 'session', sessionId } : undefined;
  }),
  // Context documents (chips on the Context tab). create / update / delete
  // all carry session_id on the input, so open the session pinned to the
  // Context tab where the chips live. list / get are reads — no artifact.
  // `add_session_context_text` is the legacy name of the create tool, kept
  // so historical transcripts still resolve an "Open" affordance.
  create_session_context_document: artifactRef('session', openSessionContextTab),
  update_session_context_document: artifactRef('session', openSessionContextTab),
  delete_session_context_document: artifactRef('session', openSessionContextTab),
  add_session_context_text: artifactRef('session', openSessionContextTab),
  edit_consult_note: artifactRef('note', (item) => {
    const sessionId = str(item.input.session_id);
    return sessionId ? { type: 'note', sessionId } : undefined;
  }),
  get_consult_note: artifactRef('note', (item) => {
    const sessionId = str(item.input.session_id);
    return sessionId ? { type: 'note', sessionId } : undefined;
  }),
  // XXX: AGE-2417 preview review — a preview is one object. The codes tools
  // (get/confirm/reject/add_session_code), the task tools and the template-editor tools open no
  // preview; the agent answers in chat instead.
  // Clinician-notes edits land on the session Context tab (the free-text
  // note editor). `edit_session_context` is the current backend name; the legacy
  // `edit_session_clinician_notes` is kept so historical transcripts still
  // resolve an artifact. The read tools (get_session_context /
  // get_session_clinician_notes) are intentionally absent — no artifact.
  edit_session_context: artifactRef('session', openSessionContextTab),
  edit_session_clinician_notes: artifactRef('session', openSessionContextTab),
  // Agent-driven "open this in the canvas". Trust ONLY the structured result
  // JSON — the backend has already validated access for those ids. Never fall
  // back to LLM tool input (that would let a crafted call navigate to
  // arbitrary patient/session records without a successful server check).
  // Polymorphic at runtime (session/note/patient/…); the typed helper would
  // force Extract<Artifact, { type: 'session' }> and reject the other variants.
  open_artifact: { type: 'session', ref: openArtifactRef },
  // Patient context edit — the id is resolved server-side and echoed on
  // the result (the input carries no id), so prefer the result payload;
  // the input lookups stay as a defensive fallback in case a future
  // harness projection injects the target id.
  update_patient_context: artifactRef('patient', (item) => {
    const patientId =
      patientIdFromResult(toolResultText(item)) ??
      str(item.input.patient_id) ??
      str(item.input.patient_profile_id);
    return patientId ? { type: 'patient', patientId } : undefined;
  }),
  // Newly-created patient profile — open the patient panel on the new id
  // (returned as `patient_profile_id` in the result payload).
  create_patient_profile: artifactRef('patient', (item) => {
    const patientId = patientIdFromResult(toolResultText(item));
    return patientId ? { type: 'patient', patientId } : undefined;
  }),
  // Completed merge — open the KEPT (target) profile so the clinician can
  // review the reconciled result and the moved sessions. The id is echoed on
  // the result payload as `target_patient_profile_id`; unlike the other
  // patient tools the merge tool also takes it as a required INPUT field, so
  // the input is a reliable fallback if the result text isn't JSON.
  merge_patient_profiles: artifactRef('patient', (item) => {
    const patientId =
      idFromResult(toolResultText(item), 'target_patient_profile_id') ??
      str(item.input.target_patient_profile_id);
    return patientId ? { type: 'patient', patientId } : undefined;
  }),
  upload_patient_document: artifactRef('patient', (item) => {
    const patientId =
      str(item.input.patient_profile_id) ?? patientIdFromResult(toolResultText(item));
    return patientId ? { type: 'patient', patientId } : undefined;
  }),
  // Completed session merge — open the resulting grouped session so the
  // clinician can review the combined note/transcript. The backend echoes the
  // container id as `group_session_id`; fall back to the anchor `session_id`
  // from the trusted input if the result text isn't JSON.
  merge_sessions: artifactRef('session', (item) => {
    const sessionId =
      idFromResult(toolResultText(item), 'group_session_id') ??
      str(item.input.session_id);
    return sessionId ? { type: 'session', sessionId } : undefined;
  }),
  create_letter: artifactRef('letter', letterArtifactFromResult),
  get_letter: artifactRef('letter', letterArtifactMatchingInputIds),
  update_letter: artifactRef('letter', letterArtifactFromResult),
  create_session_document: artifactRef('session', (item, ports) => {
    const sessionId = str(item.input.session_id);
    const documentId = idFromResult(toolResultText(item), 'document_id');
    return sessionId && documentId
      ? {
          type: 'session',
          sessionId,
          focusTab: ports.sessionTabs.document,
          focusDocumentId: documentId,
        }
      : undefined;
  }),
  // Generated-and-saved note. `session_id` may be defaulted server-side
  // (absent from the input), so prefer the result JSON which carries both
  // ids. Opens the SESSION view pinned to the Documents tab (not the
  // standalone doc viewer the export tools use) so the note keeps its
  // session context.
  generate_note_from_template: artifactRef('session', generatedSessionDocument),
  generate_prechart_document: artifactRef('session', generatedSessionDocument),
  'evidence.save_work_output': artifactRef('library-document', (item) => {
    const documentId =
      str(metadataData(item)?.job_id) ?? idFromResult(toolResultText(item), 'job_id');
    return documentId ? { type: 'library-document', documentId } : undefined;
  }),
  export_to_pdf: artifactRef('document', documentFromSessionResult),
  export_to_word: artifactRef('document', documentFromSessionResult),
  generate_word: artifactRef('document', documentFromSessionResult),
  // Generated presentation → the Slide Builder panel (auto-opens beside the
  // chat like the Evidence chat's panel does on export completion). The
  // `presentation_id` is only on the structured result metadata, and the
  // panel is flag-gated — without the flag the tool derives no artifact and
  // the pptx card is download-only.
  generate_pptx: artifactRef('presentation', (item, ports) => {
    if (!ports.isSlideBuilderEnabled()) return undefined;
    const presentationId = presentationIdFromMetadata(item);
    return presentationId ? { type: 'presentation', presentationId } : undefined;
  }),
  // The Slide Builder editors patch the deck that is already open; the ref
  // lets a timeline row re-open it. Same flag gate as generate_pptx.
  edit_slide: artifactRef('presentation', (item, ports) => {
    if (!ports.isSlideBuilderEnabled()) return undefined;
    const presentationId = presentationIdFromMetadata(item);
    return presentationId ? { type: 'presentation', presentationId } : undefined;
  }),
  add_slides_to_presentation: artifactRef('presentation', (item, ports) => {
    if (!ports.isSlideBuilderEnabled()) return undefined;
    const presentationId =
      presentationIdFromMetadata(item) ?? presentationIdFromAddedSlides(item);
    return presentationId ? { type: 'presentation', presentationId } : undefined;
  }),
  delete_slide: artifactRef('presentation', (item, ports) => {
    if (!ports.isSlideBuilderEnabled()) return undefined;
    const presentationId = presentationIdFromMetadata(item);
    return presentationId ? { type: 'presentation', presentationId } : undefined;
  }),
  save_memory: artifactRef('memory', (item) => {
    const memoryId =
      memoryIdFromMetadata(item) ?? idFromResult(toolResultText(item), 'memory_id');
    return memoryId ? { type: 'memory', memoryId } : undefined;
  }),
  create_workflow: artifactRef('workflow', (item) => {
    const workflowId = idFromResult(toolResultText(item), 'workflow_id');
    return workflowId ? { type: 'workflow', workflowId } : undefined;
  }),
  create_routine: artifactRef('workflow', (item) => {
    const workflowId = idFromResult(toolResultText(item), 'workflow_id');
    return workflowId ? { type: 'workflow', workflowId } : undefined;
  }),
  create_scheduled_workflow: artifactRef('workflow', (item) => {
    const workflowId = idFromResult(toolResultText(item), 'workflow_id');
    return workflowId ? { type: 'workflow', workflowId } : undefined;
  }),
  create_scheduled_routine: artifactRef('workflow', (item) => {
    const workflowId = idFromResult(toolResultText(item), 'workflow_id');
    return workflowId ? { type: 'workflow', workflowId } : undefined;
  }),
  // Mapping-verification surface for a completed `get_note_push_hints` read.
  // Opens keyed by (template_id, app_name) from the structured result metadata;
  // the panel resolves the CandidateMapping STORE-FIRST from the note-push
  // candidate store (populated by the timeline ingest). The panel only INFORMS
  // the clinician's answer to a pending `ask_user` mapping confirmation — it is a
  // display + override surface and NEVER auto-resolves that turn. Read only the
  // structured metadata channel, so a non-JSON confirmation string can't surface
  // an unrelated id.
  get_note_push_hints: artifactRef('note-push-verification', (item) => {
    const keys = notePushHintKeysFromMetadata(item);
    return keys
      ? {
          type: 'note-push-verification',
          templateId: keys.templateId,
          appName: keys.appName,
        }
      : undefined;
  }),
  // Recording placements ALSO opens the verification panel, so the clinician
  // sees the saved mapping even when the agent narrates a table instead of
  // re-reading via `get_note_push_hints`. Resolve identity with the SAME reader
  // the ingestion hook uses (`readNotePushRecordedData` — normalized template_id
  // + app_name from the result metadata OR the result-content JSON), so the
  // opened panel's key matches the candidate-mapping atom. Keying off the raw
  // input casing ("Cerner" vs result "cerner") would miss the mapping and
  // degrade the panel to GET-only rows, so input is only the last-resort fallback.
  record_note_push_hints: artifactRef('note-push-verification', (item) => {
    // Defer until the result block exists: on the first `complete` render it can
    // be absent, and deriving from the raw input identity now would let the
    // auto-open dedup (by tool-call id) pin the panel to a raw-cased key that the
    // later normalized result can no longer correct. Mirrors the ingestion hook.
    if (!item.result) return undefined;
    const recorded = readNotePushRecordedData(item);
    const templateId = recorded?.template_id ?? str(item.input.template_id);
    const appName = recorded?.app_name ?? str(item.input.app_name);
    return templateId && appName
      ? { type: 'note-push-verification', templateId, appName }
      : undefined;
  }),
  update_workflow: artifactRef('workflow', workflowFromResultJsonOrInput),
  update_routine: artifactRef('workflow', workflowFromResultJsonOrInput),
  schedule_workflow: artifactRef('workflow', workflowFromResultJsonOrInput),
  schedule_routine: artifactRef('workflow', workflowFromResultJsonOrInput),
  set_workflow_event_triggers: artifactRef('workflow', workflowFromResultJsonOrInput),
  set_routine_event_triggers: artifactRef('workflow', workflowFromResultJsonOrInput),
  share_workflow: artifactRef('workflow', workflowFromResultJsonOrInput),
  share_routine: artifactRef('workflow', workflowFromResultJsonOrInput),
  unshare_workflow: artifactRef('workflow', workflowFromResultJsonOrInput),
  unshare_routine: artifactRef('workflow', workflowFromResultJsonOrInput),
  // Connected-app calendar / mail — snapshot from structured result metadata.
  'integrations.calendar.list': artifactRef('calendar', calendarArtifactFromTool),
  'integrations.merge.calendar.list': artifactRef('calendar', calendarArtifactFromTool),
  'integrations.mail.search': artifactRef('mail', mailArtifactFromTool),
  'integrations.merge.mail.search': artifactRef('mail', mailArtifactFromTool),
  'integrations.merge.files.search': artifactRef('files', filesArtifactFromTool),
  'integrations.files.search': artifactRef('files', filesArtifactFromTool),
  // Availity directory — eligibility when leaf is coverage.*
  'integrations.apps.read': artifactRef('insurance', insuranceArtifactFromTool),
  'integrations.apps.write': artifactRef('insurance', insuranceArtifactFromTool),
};

/**
 * Pull plain text out of a harness tool result. `content` is `unknown` —
 * a string for most tools, or an array of content blocks
 * (`[{ type: 'text', text }]`) for providers that chunk. Only text-typed
 * content is read; structured/binary content yields `undefined` so a
 * `JSON.stringify` dump is never treated as the result payload.
 */
export const toolResultText = readToolResultText;
